// @owners { team: patients-team }
import { useCallback } from 'react';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { useSelectorShared } from '~shared/store';
import { type DeliveryWithPrescription, type NextAvailableDate, type Prescription } from '~shared/types';

export const useCartItemsRemovedEvent = () => {
  const { trackEvent } = useAnalytics();
  const origin = getOrigin();
  const isEditOrder = useSelectorShared(getIsEditingExistingOrder);

  // Prescriptions, NADs, shipment id, and deliveries are being passed in rather than fetched from Redux because we want the values BEFORE the item was removed.
  const cartItemsRemovedEvent = useCallback(
    ({
      removedPrescriptionIds,
      allPrescriptionsInCart,
      nextAvailableDatesByPrescriptionId,
      shipmentId,
      deliveriesForShipment,
      inCheckout,
    }: {
      removedPrescriptionIds: number[];
      allPrescriptionsInCart: Prescription[];
      nextAvailableDatesByPrescriptionId: Record<number, NextAvailableDate>;
      shipmentId: number | null | undefined;
      deliveriesForShipment: DeliveryWithPrescription[];
      inCheckout: boolean;
    }) => {
      const remainingPrescriptions = allPrescriptionsInCart.filter(
        (prescription) => !removedPrescriptionIds.includes(prescription.id),
      );
      const earliestDeliveryDate = remainingPrescriptions.reduce((previousEarliestDate: string, { id }) => {
        const date = nextAvailableDatesByPrescriptionId[id]?.earliest.date;
        if (previousEarliestDate) {
          return date && date < previousEarliestDate ? date : previousEarliestDate;
        } else {
          return date;
        }
      }, '');

      removedPrescriptionIds.forEach((prescriptionId) => {
        trackEvent({
          event: EVENTS.CART__ITEM_REMOVED,
          params: {
            new_shipment_next_available_date: earliestDeliveryDate,
            prescription_next_available_date: nextAvailableDatesByPrescriptionId[prescriptionId]?.earliest.date,
            edit_order_flow: isEditOrder,
            origin,
            server_side_cart: true,
          },
          additionalFields: {
            prescriptionId,
            shipmentId,
            inCheckout,
            deliveryId: deliveriesForShipment.find((delivery) => delivery.prescription_id === prescriptionId)?.id,
          },
        });
      });
    },
    [isEditOrder, origin, trackEvent],
  );

  const cartItemsRemovedFailedEvent = useCallback(
    ({
      removedPrescriptionIds,
      shipmentId,
      deliveriesForShipment,
      error,
    }: {
      removedPrescriptionIds: number[];
      shipmentId: number | null | undefined;
      deliveriesForShipment: DeliveryWithPrescription[];
      error: string;
    }) => {
      removedPrescriptionIds.forEach((prescriptionId) => {
        trackEvent({
          event: EVENTS.CART__ITEM_REMOVED_FAILED,
          params: {
            edit_order_flow: isEditOrder,
            origin,
            server_side_cart: true,
            error,
          },
          additionalFields: {
            prescriptionId,
            shipmentId,
            deliveryId: deliveriesForShipment.find((delivery) => delivery.prescription_id === prescriptionId)?.id,
          },
        });
      });
    },
    [isEditOrder, origin, trackEvent],
  );

  return {
    cartItemsRemovedEvent,
    cartItemsRemovedFailedEvent,
  };
};
